import axios from 'axios';
import useSWR ,{ mutate } from 'swr';

import { withQueryParams } from '@shared/commonFunctions';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { getError } from '@shared/utils/helpers';
import { OrderByDirectionEnum } from '@shared/hooks/useServerSide';

import { IQueueDataResponse } from '../Tabs/interfaces';
import { IMultipleReminders, IReminderModel } from './components/AddReminderModal';
import { EBureauType, ICreditReportData, IDealResponse, IStipsRequestPayload, IWorkflow, IQueueDataQueryParams, CreditBureauScoreType } from './interfaces';

const workflowService = `/${SERVICES.WORKFLOW}`;
const notificationPath = `/${SERVICES.NOTIFICATION}`;
const dealServicePath = `/${SERVICES.DEAL}`;

export const requestChangeReasons = <T>(id: string, path: string, data: T) => {
  return mutate(workflowService, async() => {
    try {
      await axios.put(`${workflowService}/${path}/${id}`, data);
      Notistack.successMessage('Reasons - changed successfully');
    } catch (err) {
		Notistack.generalError(getError(err));
    }
  });
};

export const requestMoveStepByDealId = <T>(dealId: string, data: T) => {
  return mutate(workflowService, async() => {
    try {
      const resp = await axios.post(`${workflowService}/deals/${dealId}/movestep`, data);
      if (!resp?.data?.message) {
        Notistack.successMessage('MoveStep - moved successfully');
      }
      return resp;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDeleteWFStep = (dealId: string, stepId: string) => {
  return mutate(workflowService, async() => {
    try {
      await axios.delete(`${workflowService}/deals/${dealId}/step/${stepId}`);
      Notistack.successMessage('Workflow Step - deleted successfully');
    } catch (err) {
		Notistack.generalError(getError(err));
    }
  });
};

export const requestMoveStepByLeadId = <T>(leadId: string, data: T) => {
  return mutate(workflowService, async() => {
    try {
      const resp = await axios.post(`${workflowService}/leads/${leadId}/moveleadstep`, data);
      if (!resp?.data?.message) {
        Notistack.successMessage('MoveStep - moved successfully');
      }
      return resp;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestCreateReminder = (data: IReminderModel) =>
  mutate(notificationPath, async() => {
    try {
      await axios.post(`${notificationPath}/reminders`, data);
      Notistack.successMessage('Reminder - Created Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });

export const requestUpdateReminder = (id: string, newDate: string) =>
  mutate(notificationPath, async() => {
    try {
      await axios.patch(`${notificationPath}/reminders/${id}/snooze`, String(newDate), {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      Notistack.successMessage('Reminder - Snoozed Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });

export const requestDismissReminder = (id: string) =>
  mutate(notificationPath, async() => {
    try {
      await axios.patch(`${notificationPath}/reminders/${id}/dismiss`);
      Notistack.successMessage('Reminder - dismissed Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });

export const requestDismissMultipleReminders = (data: IMultipleReminders) =>
  mutate(notificationPath, async() => {
    try {
      await axios.patch(`${notificationPath}/reminders/dismiss`, data);
      Notistack.successMessage('Reminders - Dismissed Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });

export const requestUpdateDealStatusReminder = (id: string, newDate: string) =>
  mutate(notificationPath, async() => {
    try {
      await axios.patch(`${notificationPath}/deal-lender-status-notification/${id}/snooze`, String(newDate), {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      Notistack.successMessage('Deal status reminder - Snoozed Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });

export const requestDismissDealStatusReminder = (id: string) =>
  mutate(notificationPath, async() => {
    try {
      await axios.patch(`${notificationPath}/deal-lender-status-notification/${id}/dismiss`);
      Notistack.successMessage('Deal status reminder - dismissed Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });

export const requestDismissMultipleDealStatusReminders = (data: IMultipleReminders) =>
  mutate(notificationPath, async() => {
    try {
      await axios.patch(`${notificationPath}/deal-lender-status-notification/dismiss`, data);
      Notistack.successMessage('Deal status reminders - Dismissed Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });

export const sendStipRequest = async(data: IStipsRequestPayload) => {
  try {
    await axios.post(`${SERVICES.DOCUMENT}/stips/request`, data);
    Notistack.successMessage('STIPS Requests - send successfully');
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const requestChangeConditions = <T>(id: string, isConditionLp = false, data: T) => {
  return mutate(workflowService, async () => {
    try {
      await axios.put(`${workflowService}/dealconditions/${id}?isConditionLp=${isConditionLp}`, data);
      Notistack.successMessage('Conditions - changed successfully');
    } catch (err) {
		Notistack.generalError(getError(err));
    }
  });
};

export const requestGetDealIdByNumericId = (dealNumericId: number) => {
  return mutate(dealServicePath, async() => {
    try {
      const response: IDealResponse = await axios.post(`${dealServicePath}/dealIds-by-numericid`, [dealNumericId]);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const getCreditReportData = async (
  contactId: string,
  bureauType: EBureauType,
  scoreType: CreditBureauScoreType | null,
  pullLatestVersion = false,
) => {
  try {
    const resp = await axios.get<ICreditReportData>(
      `${SERVICES.EXTERNAL}/credit-reports/${contactId}?bureauType=${bureauType}&scoreType=${scoreType??''}&pullLatestVersion=${pullLatestVersion}`
    );

    return resp?.data;
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const requestGetQueueData = async (url: string, recordNumber: number, queueId: string, viewAsUserId: string, orderBy?: string, orderByDirection?: OrderByDirectionEnum) => {
  try {
    const queryParams: IQueueDataQueryParams = {
      queueId,
      pageSize: 1,
      offSet: recordNumber,
      viewAsUserId,
    };
    if (orderBy) queryParams.orderBy = orderBy;
    if (orderByDirection) queryParams.orderByDirection = orderByDirection;

    const response = await axios.get<IQueueDataResponse>(withQueryParams(url, queryParams ,false));

        return response?.data;
      } catch (err) {
        Notistack.generalError(getError(err));
      }
};

export const useLoadCurrentWorkFlowLead = (leadId: string) => {
  const   { data: currentWorkflowLead,
     isLoading: leadWorkflowLoading,
     mutate: reloadCurrentWorkFlowLead,
   } = useSWR<IWorkflow>(Boolean(leadId) && `${SERVICES.WORKFLOW}/leads/${leadId}/currentworkflowlead`);
 return { currentWorkflowLead, leadWorkflowLoading,reloadCurrentWorkFlowLead };
};